import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { sendEmail } from "./emailProcessing";
import ReCAPTCHA from "react-google-recaptcha";
import "./ContactForm.css";

// Import translations
import enTranslations from "../en.json";
import deTranslations from "../de.json";

const ContactForm = () => {
  const { t, i18n } = useTranslation();

  // Add translations for this component
  i18n.addResourceBundle("en", "contact", enTranslations, true, true);
  i18n.addResourceBundle("de", "contact", deTranslations, true, true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [captchaValue, setCaptchaValue] = useState(null);
  const captchaRef = useRef(null);
  const submissionPending = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleClosePopup = () => {
    setPopup({ show: false, type: "", message: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submissionPending.current) return;

    // Ensure required fields are filled
    const { name, email, message } = formData;
    const formValid =
      name.trim() !== "" && email.trim() !== "" && message.trim() !== "";
    if (!formValid) {
      setPopup({
        show: true,
        type: "error",
        message: t("contact:required_fields_error"),
      });
      return;
    }

    // Only check captcha if reCAPTCHA is configured
    if (process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY && !captchaValue) {
      setPopup({
        show: true,
        type: "error",
        message: t("contact:captcha_error"),
      });
      return;
    }

    submissionPending.current = true;
    setIsSubmitting(true);
    try {
      await sendEmail(formData, captchaValue);
      setPopup({
        show: true,
        type: "success",
        message: t("contact:success_message"),
      });
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("EmailJS submission failed", {
        status: err.status,
        reason: err.text || err.message,
      });
      setPopup({
        show: true,
        type: "error",
        message: t("contact:error_message"),
      });
    } finally {
      captchaRef.current?.reset();
      setCaptchaValue(null);
      submissionPending.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form-container">
      {popup.show && (
        <div className={`popup-overlay active`} onClick={handleClosePopup}>
          <div className={`popup-message ${popup.type}`}>
            <p>{popup.message}</p>
            <button onClick={handleClosePopup}>
              {t("contact:close_button")}
            </button>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="contact-form" aria-busy={isSubmitting}>
        <div className="form-header">
          <h2
            className="form-title focus-highlight"
            tabIndex={0}
            role="text"
            aria-label={t(
              "contact:form_title_aria",
              "Formulartitel: {{title}}",
              { title: t("contact:form_title") }
            )}
            style={{ pointerEvents: "none" }}
            onFocus={(e) => {
              e.target.style.pointerEvents = "auto";
            }}
            onBlur={(e) => {
              e.target.style.pointerEvents = "none";
            }}
          >
            {t("contact:form_title")}
          </h2>
        </div>
        <div className="form-group">
          <label htmlFor="name">{t("contact:form_name_label")}</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="email">{t("contact:form_email_label")}</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="message">{t("contact:form_message_label")}</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows="5"
            required
          ></textarea>
        </div>
        <div className="captcha-submit-container">
          <div className="recaptcha-container">
            {process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY && (
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
                onExpired={() => setCaptchaValue(null)}
                onErrored={() => setCaptchaValue(null)}
              />
            )}
          </div>
          <button
            type="submit"
            className="submit-button focus-highlight"
            tabIndex={0}
            aria-label={t(
              "contact:submit_button_aria",
              "Formular absenden: {{label}}",
              { label: t("contact:form_submit_button") }
            )}
            disabled={
              isSubmitting ||
              // disable if required fields missing
              !(
                formData.name.trim() &&
                formData.email.trim() &&
                formData.message.trim()
              ) ||
              // also disable if captcha is configured but not completed
              (process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY && !captchaValue)
            }
          >
            {t(isSubmitting ? "contact:form_sending" : "contact:form_submit_button")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
